import { useTranslation } from 'react-i18next';

import { formatChronicleDate } from '@/utils/format';

type ChroniclePrecision = 'day' | 'month' | 'year';

const precisionOf = (value: unknown): ChroniclePrecision =>
  value === 'month' || value === 'year' || value === 'day' ? value : 'day';

const chronicleDate = (row: Record<string, unknown>, locale: string) => {
  const occurredOn = typeof row.occurredOn === 'string' ? row.occurredOn : '';
  if (!occurredOn) return '—';
  return formatChronicleDate(occurredOn, precisionOf(row.datePrecision), { locale });
};

export const ChronicleDocumentView = ({
  row,
  locale,
}: {
  row: Record<string, unknown>;
  locale: string;
}) => {
  const { t } = useTranslation('reports');
  const body = typeof row.body === 'string' ? row.body.trim() : '';
  const title = typeof row.title === 'string' ? row.title : '';
  const category = typeof row.categoryName === 'string' ? row.categoryName : '';
  const status = typeof row.status === 'string' ? t(`value.${row.status}`, row.status) : '';
  const visibility =
    typeof row.visibility === 'string' ? t(`value.${row.visibility}`, row.visibility) : '';

  return (
    <article className="mx-auto max-w-3xl px-6 py-8">
      <p className="text-caption text-fg-secondary">
        {[chronicleDate(row, locale), category].filter((part) => part.length > 0).join(' · ')}
      </p>
      <h3 className="mt-2 text-section-title font-semibold text-fg">{title}</h3>
      <p className="mt-2 text-caption text-fg-secondary">
        {[status, visibility].filter((part) => part.length > 0).join(' · ')}
      </p>
      <p className="mt-6 whitespace-pre-wrap text-body text-fg">{body || '—'}</p>
    </article>
  );
};

export const ChronicleTimelineView = ({
  rows,
  locale,
}: {
  rows: Record<string, unknown>[];
  locale: string;
}) => {
  const { t } = useTranslation('reports');
  const years: { year: string; rows: Record<string, unknown>[] }[] = [];
  for (const row of rows) {
    const occurredOn = typeof row.occurredOn === 'string' ? row.occurredOn : '';
    const year = occurredOn.slice(0, 4) || '—';
    const group = years.find((item) => item.year === year);
    if (group) group.rows.push(row);
    else years.push({ year, rows: [row] });
  }

  return (
    <div className="flex flex-col gap-8 px-4 py-4">
      {years.map((group) => (
        <section key={group.year}>
          <h3 className="mb-4 text-card-title font-semibold text-fg">{group.year}</h3>
          <ol className="relative ms-2 border-s border-border">
            {group.rows.map((row, index) => {
              const title = typeof row.title === 'string' ? row.title : '';
              const category = typeof row.categoryName === 'string' ? row.categoryName : '';
              const body = typeof row.body === 'string' ? row.body.trim() : '';
              const status = typeof row.status === 'string' ? t(`value.${row.status}`, row.status) : '';
              const visibility =
                typeof row.visibility === 'string'
                  ? t(`value.${row.visibility}`, row.visibility)
                  : '';
              return (
                <li key={`${group.year}-${index}`} className="relative pb-6 ps-6 last:pb-0">
                  <span
                    className="absolute -start-1.5 top-1.5 size-3 rounded-full border-2 border-brand bg-background"
                    aria-hidden
                  />
                  <p className="text-caption text-fg-secondary">
                    {[chronicleDate(row, locale), category].filter((part) => part.length > 0).join(' · ')}
                  </p>
                  <p className="mt-1 text-body font-medium text-fg">{title}</p>
                  <p className="mt-1 text-caption text-fg-secondary">
                    {[status, visibility].filter((part) => part.length > 0).join(' · ')}
                  </p>
                  {body ? (
                    <p className="mt-2 line-clamp-4 whitespace-pre-wrap text-body-sm text-fg-secondary">
                      {body}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </section>
      ))}
    </div>
  );
};
